import { useMemo } from "react";
import { useReportingQuery } from "web_myplann/Api/Finances/useReportingQuery";
import { PortfolioReportingOverviewTypeEnum } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import type { DashboardType } from "../Components/DashboardTitleCard";

type DashboardConfig = {
	type: PortfolioReportingOverviewTypeEnum;
	gradientFrom: string;
	gradientTo: string;
};

const dashboardConfigs: DashboardConfig[] = [
	{
		type: PortfolioReportingOverviewTypeEnum.Total,
		gradientFrom: "#FA674E",
		gradientTo: "#F18E29",
	},
	{
		type: PortfolioReportingOverviewTypeEnum.Investment,
		gradientFrom: "#CC70F7",
		gradientTo: "#F572D5",
	},
	{
		type: PortfolioReportingOverviewTypeEnum.InvestmentProperty,
		gradientFrom: "#292D74",
		gradientTo: "#16B8F3",
	},
	{
		type: PortfolioReportingOverviewTypeEnum.Cash,
		gradientFrom: "#01343A",
		gradientTo: "#00844B",
	},
	{
		type: PortfolioReportingOverviewTypeEnum.Other,
		gradientFrom: "#112244",
		gradientTo: "#24547D",
	},
];

const OverviewTypeResources = Resources.Dashboard.OverviewType;

export const useDashboardData = () => {
	const { t } = useResource();
	const { data: reportingData, isLoading, isFetching } = useReportingQuery();

	const dashboards = useMemo<DashboardType[]>(() => {
		const items = reportingData?.data;

		return dashboardConfigs.flatMap((config, index) => {
			const apiItem = items?.find((item) => item.type === config.type);
			if (!apiItem || apiItem.value == null || apiItem.value === 0) return [];

			return [
				{
					id: index,
					overviewType: config.type,
					type: t(OverviewTypeResources[config.type]),
					value: apiItem.value,
					currency: apiItem.currency ?? "",
					evaluation: apiItem.evaluation ?? undefined,
					gradientFrom: config.gradientFrom,
					gradientTo: config.gradientTo,
				},
			];
		});
	}, [reportingData, t]);

	const isEmpty = dashboards.length === 0;

	return { dashboards, isLoading, isFetching, isEmpty };
};

export const getDashboardIndexByType = (
	dashboards: DashboardType[],
	type: PortfolioReportingOverviewTypeEnum,
) => dashboards.findIndex((d) => d.overviewType === type);
